/* eslint-disable */
import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { useStore } from '../../store/store';
import { useNav } from '../../hooks/nav';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import Analytics from '@web-solutions/module-analytics';
import exclamation from '../../assets/images/exclamation.png';
import { t } from '@web-solutions/module-localization';
import Button from 'src/onboarding/components/ui/button/Button';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import RulerPicker from 'src/onboarding/components/ui/ruler-picker/RulerPicker';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';

function Page() {
  const weight = useStore((state) => state.weight);
  const height = useStore((state) => state.height);
  const setWeight = useStore((state) => state.setWeight);
  const { bmi, setBmi } = useStore((state) => state);
  const { onNext, onPrev, currentIndex } = useNav();
  const [notification, setNotification] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  useEffect(() => {
    const savedNotification = localStorage.getItem('weightNotification');
    if (savedNotification) {
      setNotification(JSON.parse(savedNotification));
    }
  }, []);

  const handleWeightChange = (newValue: number, unit: 'kg' | 'lbs') => {
    setWeight({ type: unit, [unit]: String(newValue) });
  };

  const handleUserInteraction = () => {
    setUserInteracted(true);
  };

  useEffect(() => {
    if (height.type === 'ft') {
      setWeight({ ...weight, type: 'lbs' });
    } else {
      setWeight({ ...weight, type: 'kg' });
    }
  }, []);

  const handleInteractionEnd = () => {
    if (
      userInteracted &&
      ((weight?.kg && weight.kg.length >= 2) || (weight?.lbs && weight.lbs.length >= 2))
    ) {
      calculateBmi();
    }
  };

  const calculateBmi = () => {
    let weightInKg = weight.type === 'lbs' ? +weight.lbs * 0.453592 : +weight.kg;

    let heightInMeters;

    if (height.type === 'ft') {
      const [feetStr, inchesStr = '0'] = height.ft.split('.');
      const feet = parseInt(feetStr, 10);
      const inches = parseInt(inchesStr, 10);

      const totalInches = feet * 12 + (inches || 0);
      heightInMeters = totalInches * 0.0254;
    } else if (height.type === 'cm') {
      heightInMeters = +height.cm / 100;
    }

    if (heightInMeters > 0) {
      const calculatedBmi = (weightInKg / heightInMeters ** 2).toFixed(1);
      setBmi(calculatedBmi);
      determineNotification(calculatedBmi);
    }
  };

  const determineNotification = (bmi) => {
    let newNotification = null;

    if (bmi < 18.5) {
      newNotification = {
        title: t('onboarding.weight.notifications.underweight.title').replace('{VALUE}', bmi),
        message: t('onboarding.weight.notifications.underweight.message'),
        type: t('onboarding.weight.notifications.underweight.type'),
      };
    } else if (bmi >= 18.5 && bmi <= 24.9) {
      newNotification = {
        title: t('onboarding.weight.notifications.normal.title').replace('{VALUE}', bmi),
        message: t('onboarding.weight.notifications.normal.message'),
        type: t('onboarding.weight.notifications.normal.type'),
      };
    } else if (bmi >= 25 && bmi <= 29.9) {
      newNotification = {
        title: t('onboarding.weight.notifications.overweight.title').replace('{VALUE}', bmi),
        message: t('onboarding.weight.notifications.overweight.message'),
        type: t('onboarding.weight.notifications.overweight.type'),
      };
    } else if (bmi >= 30) {
      newNotification = {
        title: t('onboarding.weight.notifications.obese.title').replace('{VALUE}', bmi),
        message: t('onboarding.weight.notifications.obese.message'),
        type: t('onboarding.weight.notifications.obese.type'),
      };
    }

    if (newNotification) {
      setNotification(newNotification);
      localStorage.setItem('weightNotification', JSON.stringify(newNotification));
    }
  };

  return (
    <div className={'page'}>
      {currentIndex !== 0 && (
        <div className={'back'} onClick={onPrev}>
          <BackIcon />
        </div>
      )}
      <div className={'wrapper-info'}>
        <Title tag='h2'>{t('onboarding.weight.title')}</Title>
        <Text size='m' medium className={'description'} style={{ padding: 0 }}>
          {weight.type === 'lbs'
            ? t('onboarding.weight.lbsSubTitle_new')
            : t('onboarding.weight.kgSubTitle_new')}
        </Text>
      </div>

      <div className={'controls controls_new'}>
        <div className={'tabs'}>
          <span
            className={cn({ active: weight.type === 'kg' })}
            onClick={() => {
              setWeight({ ...weight, type: 'kg', kg: '' });
            }}
          >
            {t('onboarding.weight.kg')}
          </span>
          <span
            className={cn({ active: weight.type === 'lbs' })}
            onClick={() => {
              setWeight({ ...weight, type: 'lbs', lbs: '' });
            }}
          >
            {t('onboarding.weight.lbs')}
          </span>
        </div>
        {weight.type === 'kg' && (
          <RulerPicker
            min={30}
            max={200}
            step={1}
            onChange={(newValue) => handleWeightChange(newValue, 'kg')}
            initialValue={Number(weight.kg) || 90}
            unit='kg'
            stepWidth={15}
            divisions={5}
            onUserInteract={handleUserInteraction}
            onInteractionEnd={handleInteractionEnd}
          />
        )}

        {weight.type === 'lbs' && (
          <RulerPicker
            min={67}
            max={447}
            step={1}
            onChange={(newValue) => handleWeightChange(Math.round(newValue), 'lbs')}
            initialValue={Number(weight.lbs) || 200}
            unit='lbs'
            stepWidth={7}
            divisions={10}
            onUserInteract={handleUserInteraction}
            onInteractionEnd={handleInteractionEnd}
          />
        )}

      </div>

      {notification && (
        <div className={`notification ${notification.type}`}>
          {
            <div className={'title-container'}>
              {notification.type !== 'normal' && (
                <img className={'exclamation'} src={exclamation} />
              )}
              <Text size='s' semibold style={{ textAlign: 'left' }}>
                {notification.title}
              </Text>
            </div>
          }
          <Caption className={'notification-description'}>{notification.message}</Caption>
        </div>
      )}

      <div className={'next-wrapper'}>
        <Button
          title={t('onboarding.weight.continue')}
          onClick={() => {
            Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
              page: 'weight',
              answer: weight.type === 'kg' ? weight.kg : Math.round(+weight.lbs * 0.453592),
            });
            onNext();
          }}
        />
      </div>
    </div>
  );
}

export default Page;
